import React, { createContext, useContext } from 'react'
import { Loader } from '@reapit/elements/deprecated/loader'
import { useAdminHealthCheck } from './api/health/get'
import { AccessDeniedPage } from './pages/access-denied'
import { TechnicalDifficultiesPage } from './pages/technical-difficulties'

interface AccessModeContextValue {
  /** True when the API is in "readonly" mode (TEST/PROD) — write affordances should be hidden, not just left
   * to fail server-side. Defaults to true (the safe default) if read before the health check resolves. */
  isReadOnly: boolean
}

const AccessModeContext = createContext<AccessModeContextValue>({ isReadOnly: true })

/**
 * True when the CURRENT form version is production-locked (live in Production — never editable again,
 * anywhere, per the backend guard on every mutating endpoint). Distinct from the env-wide `isReadOnly`
 * above: this is per-version, set by `VersionLockProvider` around the builder route once its version data
 * loads. Folded into `useAccessMode`'s `isReadOnly` below so every existing call site picks it up for free.
 */
const VersionLockContext = createContext<boolean>(false)

/** Wrap the builder route's content in this, passing the loaded version's `isProductionLocked`. */
export const VersionLockProvider: React.FC<React.PropsWithChildren<{ isLocked: boolean }>> = ({
  isLocked,
  children,
}) => <VersionLockContext.Provider value={isLocked}>{children}</VersionLockContext.Provider>

/**
 * Gates the app behind a single GET /api/health check: shows AccessDeniedPage on a 403 (not on the admin
 * whitelist), TechnicalDifficultiesPage on any other failure, a loader while pending, and otherwise renders
 * children wrapped in a context exposing the resolved accessMode — the same value the backend's
 * ReadOnlyEnforcementMiddleware enforces, so the UI and the API can never disagree about what's editable.
 */
export const AccessModeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const health = useAdminHealthCheck()

  if (health.state === 'loading') {
    return <Loader fullPage />
  }

  if (health.state === 'unauthorized') {
    return <AccessDeniedPage />
  }

  if (health.state === 'unhealthy') {
    return <TechnicalDifficultiesPage />
  }

  return (
    <AccessModeContext.Provider value={{ isReadOnly: health.accessMode === 'readonly' }}>
      {children}
    </AccessModeContext.Provider>
  )
}

/**
 * Whether write affordances should be hidden right now — either because the whole environment is
 * read-only (TEST/PROD), or because the current form version is production-locked (see
 * `VersionLockProvider`). Callers don't need to know which reason applies; both mean "hide, don't disable."
 */
export const useAccessMode = (): AccessModeContextValue => {
  const { isReadOnly } = useContext(AccessModeContext)
  const isVersionLocked = useContext(VersionLockContext)
  return { isReadOnly: isReadOnly || isVersionLocked }
}
